"use client"

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import { supabase } from "@/lib/supabaseClient"
import type { ConsumerIaStage } from "@/lib/career/consumerIa"

// Resolves the consumer-IA flag for the client shells, which swap their nav to the four destinations
// (For You / Explore / My List / Me) when `enabled`.
//
// Why client-side: Careers auth is Bearer-first, so a server cookie read can be stale/expired at
// render time (it was, which left the flag stuck off). Here the client sends its FRESH access token
// to GET /api/career/consumer-ia, the reliable path every Careers API uses. To avoid a flash for a
// returning user, the last-known "enabled" is cached in localStorage and seeds the first paint, then
// the fetch confirms it. `?classic=1` (sticky) forces the legacy shell; `?classic=0` / `?new=1` clears.

interface ConsumerIaValue {
  enabled: boolean
  stage: ConsumerIaStage
}

const ConsumerIaCtx = createContext<ConsumerIaValue>({ enabled: false, stage: "off" })

export function useConsumerIa(): ConsumerIaValue {
  return useContext(ConsumerIaCtx)
}

const CACHE_KEY = "careers.ia_enabled"
const CLASSIC_KEY = "careers.classic"

export function ConsumerIaProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false)
  const [stage, setStage] = useState<ConsumerIaStage>("internal")
  const [classic, setClassic] = useState(false)

  // First paint: seed from cache + resolve the ?classic override (sticky per device).
  useEffect(() => {
    try {
      if (localStorage.getItem(CACHE_KEY) === "1") setEnabled(true)
      const sp = new URLSearchParams(window.location.search)
      const q = sp.get("classic")
      if (q === "1") {
        localStorage.setItem(CLASSIC_KEY, "1")
        setClassic(true)
      } else if (q === "0" || sp.get("new") === "1") {
        localStorage.removeItem(CLASSIC_KEY)
        setClassic(false)
      } else if (localStorage.getItem(CLASSIC_KEY) === "1") {
        setClassic(true)
      }
    } catch {
      /* storage blocked */
    }
  }, [])

  // Authoritative resolve via the Bearer-authed endpoint; update + re-cache.
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const { data } = await supabase.auth.getSession()
        const token = data.session?.access_token
        if (!token) {
          if (active) setEnabled(false)
          try { localStorage.removeItem(CACHE_KEY) } catch { /* ignore */ }
          return
        }
        const r = await fetch("/api/career/consumer-ia", { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" })
        if (!r.ok) return
        const j = (await r.json()) as { ok?: boolean; enabled?: boolean; stage?: ConsumerIaStage }
        if (!active || !j?.ok) return
        setEnabled(Boolean(j.enabled))
        if (j.stage) setStage(j.stage)
        try {
          if (j.enabled) localStorage.setItem(CACHE_KEY, "1")
          else localStorage.removeItem(CACHE_KEY)
        } catch { /* ignore */ }
      } catch {
        /* network/auth failure — leave the cached/seeded value, fail toward legacy */
      }
    })()
    return () => { active = false }
  }, [])

  const value = useMemo<ConsumerIaValue>(() => ({ enabled: enabled && !classic, stage }), [enabled, classic, stage])
  return <ConsumerIaCtx.Provider value={value}>{children}</ConsumerIaCtx.Provider>
}
